import { ColumnInfo, WebsiteColumnResult, JobConfig } from '../types/rankAList';

interface UseRankAListHandlersProps {
  setUploadId: (id: string) => void;
  setUploadedFile: (file: File | undefined) => void;
  setTotalRows: (rows: number | undefined) => void;
  setColumns: (columns: ColumnInfo[]) => void;
  setStep: (step: number) => void;
  setExpandedSteps: (steps: number[]) => void;
  setWebsiteColumnResults: (results: WebsiteColumnResult[]) => void;
  setSelectedWebsiteColumns: (columns: string[]) => void;
  setSearchForMissing: (search: boolean) => void;
  setPassthroughColumns: (columns: string[]) => void;
  setApproach: (approach: 'prediction' | 'similarity') => void;
  setLabelColumn: (column: string) => void;
  setAvailableLabels: (labels: string[]) => void;
  setJobId: (id: string) => void;
  uploadId: string;
  selectedWebsiteColumns: string[];
  passthroughColumns: string[];
  searchForMissing: boolean;
  approach: 'prediction' | 'similarity';
  labelColumn: string;
  positiveLabels: string[];
  negativeLabels: string[];
}

export const useRankAListHandlers = (props: UseRankAListHandlersProps) => {
  const handleFileUpload = async (file: File) => {
    const mockUploadId = 'upload_' + Date.now();
    const mockColumns = [
      { name: 'company_name', type: 'string', sampleValues: ['Acme Corp', 'TechStart Inc'] },
      { name: 'website', type: 'string', sampleValues: ['acme.com', 'techstart.io'] },
      { name: 'category', type: 'string', sampleValues: ['B2B', 'B2C'] },
      { name: 'revenue', type: 'number', sampleValues: ['1000000', '500000'] },
      { name: 'employees', type: 'number', sampleValues: ['50', '25'] },
      { name: 'location', type: 'string', sampleValues: ['San Francisco', 'New York'] }
    ];
    const mockTotalRows = 1250;
    
    props.setUploadId(mockUploadId);
    props.setUploadedFile(file);
    props.setTotalRows(mockTotalRows);
    props.setColumns(mockColumns);
    props.setStep(2);
    props.setExpandedSteps([2]);
  };

  const handleWebsiteColumnAnalysis = async (selectedColumns: string[], searchMissing: boolean) => {
    const mockResults = selectedColumns.map(col => ({
      column: col,
      uniqueCount: Math.floor(Math.random() * 1000) + 100
    }));
    
    props.setWebsiteColumnResults(mockResults);
    props.setSelectedWebsiteColumns(selectedColumns);
    props.setSearchForMissing(searchMissing);
    props.setStep(3);
    props.setExpandedSteps([3]);
  };

  const handlePassthroughSelection = (selectedColumns: string[]) => {
    props.setPassthroughColumns(selectedColumns);
    props.setStep(4);
    props.setExpandedSteps([4]);
  };

  const handleApproachSelection = (selectedApproach: 'prediction' | 'similarity') => {
    props.setApproach(selectedApproach);
    if (selectedApproach === 'prediction') {
      props.setStep(5);
      props.setExpandedSteps([5]);
    } else {
      props.setStep(5);
      props.setExpandedSteps([5]);
    }
  };

  const handleLabelColumnSelection = async (column: string) => {
    const mockLabels = ['B2B', 'B2C', 'Enterprise', 'SMB', 'Startup'];
    
    props.setLabelColumn(column);
    props.setAvailableLabels(mockLabels);
  };

  const handleRunJob = async () => {
    const config: JobConfig = {
      uploadId: props.uploadId,
      websiteColumns: props.selectedWebsiteColumns,
      passthroughColumns: props.passthroughColumns,
      searchForMissing: props.searchForMissing,
      approach: props.approach,
      labelColumn: props.approach === 'prediction' ? props.labelColumn : undefined,
      positiveLabels: props.approach === 'prediction' ? props.positiveLabels : undefined,
      negativeLabels: props.approach === 'prediction' ? props.negativeLabels : undefined
    };

    const mockJobId = 'job_' + Date.now();
    props.setJobId(mockJobId);
    const nextStep = props.approach === 'prediction' ? 6 : 6;
    props.setStep(nextStep);
    props.setExpandedSteps([nextStep]);
  };

  const handleJobComplete = () => {
    const nextStep = props.approach === 'prediction' ? 7 : 7;
    props.setStep(nextStep);
    props.setExpandedSteps([nextStep]);
  };

  return {
    handleFileUpload,
    handleWebsiteColumnAnalysis,
    handlePassthroughSelection,
    handleApproachSelection,
    handleLabelColumnSelection,
    handleRunJob,
    handleJobComplete,
  };
}; 